import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress,
  Stepper, Step, StepButton, Button, DialogContentText, Box
} from '@material-ui/core';
import { toast } from 'react-toastify';
import { ArrowBackIos, ArrowForwardIos, Save } from '@material-ui/icons';
import { useTranslation } from 'react-i18next';
import DownloadXls from './form/components/content/components/Massive/model';
import UploadXls from './form/components/content/components/Massive/upload';
import SaveXls from './form/components/content/components/Massive/save';
import TeamData from 'services/dataService/TeamData';
import { exportToXlsx } from 'helpers/reportExport';
import { useSelector } from 'react-redux';

export default function TeamMassiveModal({ open, onClose }) {
  const { t } = useTranslation(['form', 'button']);
  const [activeStep, setActiveStep] = useState(0);
  const [completed, setCompleted] = useState({});
  const [uploadedData, setUploadedData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const [uploadedFile, setUploadedFile] = useState(null); 
  const [errorExportData, setErrorExportData] = useState(null);
  const [showConfirmation, setShowConfirmation] = React.useState(false);
  const [validUsers, setValidUsers] = useState([]);

  const { company } = useSelector((state) => state);

  React.useEffect(() => {
    if (open) {
      setActiveStep(0);
      setCompleted({});
      setUploadedData([]);
      setIsLoading(false);
      setValidationErrors([]);
      setUploadedFile(null);
      setErrorExportData(null);
      setShowConfirmation(false);
      setValidUsers([]);
    }
  }, [open]);

  const handleUploadComplete = (file) => {
    setUploadedFile(Array.isArray(file) ? file[0] : file); 
  };

  const formatCSVResponse = (records) =>
    records.map(({ matchTeam, ...record }) => ({
      ...record,
      reject_reason: Array.isArray(record.reject_reason)
        ? record.reject_reason
            .map((reason) => {
              const [value, field] = reason.split(',');
              return t(`files:${value}`, { field });
            })
            .join('; ')
        : record.reject_reason,
    }));

    const handleClickConfirmSave = async () => {
      try {
        const { data } = await TeamData.saveMassiveUserTeams(
          validUsers,
          company?.domain_company
        );
        toast.success(t('toastify:Teams.attribuition_success'));
        setShowConfirmation(false);
        onClose();
      } catch (err) {
         if (err.response?.status === 403) {
            toast.error(t('toastify:commons.toast_error_403'));
          } else {
            toast.error(t('toastify:commons.toast_error_api'));
          }
        }
    };

    const handleSave = () => {
      if (validUsers.length === 0) {
        alert('Nenhum usuário válido');
        return;
      }
      setShowConfirmation(true);    
    };

  const handleNextStep = async () => {
    if (activeStep === 1) {
      if (!uploadedFile) {
        toast.error('Por favor, envie a planilha antes de continuar.');
        return;
      }
      
      setIsLoading(true); 
      
      try {
        const fileToUpload = Array.isArray(uploadedFile) ? uploadedFile[0] : uploadedFile;
        const formData = new FormData();
        formData.append('file', fileToUpload);
  
        const { data } = await TeamData.verifyMassiveUserTeams(
          formData,
          company?.domain_company
        );
  
        if (data.invalidUsers?.length) {
          const formattedErrors = formatCSVResponse(data.invalidUsers);
          const cols = new Set(data.invalidUsers.flatMap((i) => Object.keys(i)));
  
          const extension =
            data?.extension?.toLocaleLowerCase() === 'csv' ? 'csv' : 'excel';
  
          setErrorExportData({
            extension,
            cols: [...cols],
            data: formattedErrors,
          });
  
        } else {
          setErrorExportData(null); 
        }
  
        formData.delete('file');
  
        setValidationErrors(data || []);
      } catch (err) {
        console.error('Erro ao validar planilha:', err);
        toast.error('Erro ao validar a planilha. Tente novamente.');
        return;
      } finally {
        setIsLoading(false);
      }
    }
  
    setActiveStep((prev) => prev + 1);
  };
  
  const handlePrevStep = () => setActiveStep((prev) => prev - 1);
  const handleStep = (step) => () => setActiveStep(step);

  const components = [
    { title: 'Modelo', component: <DownloadXls /> },
    { title: 'Upload', component: <UploadXls onUploadComplete={handleUploadComplete} /> },
    {
      title: 'Salvar',
      component: (
        <SaveXls
          data={uploadedData}
          errors={validationErrors}
          exportData={errorExportData}
          onSave={(users) => setValidUsers(users)} 
        />
      ),
    },
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      PaperProps={{
        style: {
          minHeight: 520,
          maxHeight: 520,
        },
      }}
    >
      <DialogTitle>
        <Stepper activeStep={activeStep} alternativeLabel>
          {components.map((step, index) => (
            <Step key={index} completed={completed[index]}>
              <StepButton onClick={handleStep(index)}>
                {step.title}
              </StepButton>
            </Step>
          ))}
        </Stepper>
      </DialogTitle>
      <DialogContent>
          {isLoading && activeStep === 1 ? (
            <Box display="flex" justifyContent="center" alignItems="center" height={300}>
              <CircularProgress size={48} />
            </Box>
          ) : (
            components[activeStep].component
          )}
        </DialogContent>
      <DialogActions>
        {activeStep > 0 && (
          <Button
            onClick={handlePrevStep}
            startIcon={<ArrowBackIos />}
            color="primary"
            variant="outlined"
          >
            {t('button:commons.btn_back')}
          </Button>
        )}
        {activeStep < components.length - 1 && (
          <Button
            onClick={handleNextStep}
            startIcon={<ArrowForwardIos />}
            color="primary"
            variant="outlined"
          >
            {t('button:commons.btn_foward')}
          </Button>
        )}
        {activeStep === components.length - 1 && (
          <Button
            onClick={handleSave}
            startIcon={<Save />}
            color="primary"
            variant="contained"
          >
              {isLoading ? 'Validando...' : 'Salvar'}
          </Button>
        )}
      </DialogActions>
      <Dialog
            open={showConfirmation}
            onClose={() => setShowConfirmation(false)}
          >
            <DialogTitle>Confirmação</DialogTitle>
            <DialogContent>
              <DialogContentText>
                Deseja realmente salvar os dados?
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setShowConfirmation(false)}>Cancelar</Button>
              <Button onClick={handleClickConfirmSave} color="primary">
                Salvar
              </Button>
            </DialogActions>
          </Dialog>
    </Dialog>
  );
}
